import { useAuthStore } from '../stores/authStore';

export class SessionManager {
  private static instance: SessionManager;
  private timeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private checkIntervalId: NodeJS.Timeout | null = null;
  private lastActivity: number = Date.now();
  private warningShown: boolean = false;
  private isRunning: boolean = false;

  // Session configuration
  private readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  private readonly WARNING_TIME = 5 * 60 * 1000; // 5 minutes before logout
  private readonly CHECK_INTERVAL = 30 * 1000; // Check every 30 seconds
  private readonly TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // Refresh token 5 minutes before expiry

  private constructor() {
    this.bindEvents();
    // Don't start activity check automatically - let start() method handle it
  }

  static getInstance(): SessionManager {
    if (!SessionManager.instance) {
      SessionManager.instance = new SessionManager();
    }
    return SessionManager.instance;
  }

  private bindEvents(): void {
    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, this.resetTimer.bind(this), { passive: true });
    });

    // Listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkTokenValidity();
      }
    });

    // Listen for storage changes (logout in another tab)
    window.addEventListener('storage', (e) => {
      if (e.key === 'access_token' && !e.newValue) {
        // Token was removed in another tab
        this.handleLogout('Session ended in another tab');
      }
    });
  }

  private resetTimer(): void {
    if (!this.isRunning) return;

    this.lastActivity = Date.now();
    this.warningShown = false;
    
    // Clear existing timers
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }

    // Set warning timer
    this.warningTimeoutId = setTimeout(() => {
      this.showWarning();
    }, this.INACTIVITY_TIMEOUT - this.WARNING_TIME);

    // Set logout timer
    this.timeoutId = setTimeout(() => {
      this.handleLogout('Session expired due to inactivity');
    }, this.INACTIVITY_TIMEOUT);
  }

  private showWarning(): void {
    if (this.warningShown) return;
    
    this.warningShown = true;
    const remainingTime = Math.ceil(this.WARNING_TIME / 1000 / 60); // minutes
    
    // Create warning modal
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50';
    modal.innerHTML = `
      <div class="bg-white rounded-lg p-6 max-w-md mx-4">
        <div class="flex items-center mb-4">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.996-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-lg font-medium text-gray-900">Session Warning</h3>
          </div>
        </div>
        <div class="mb-4">
          <p class="text-sm text-gray-700">
            Your session will expire in ${remainingTime} minute${remainingTime !== 1 ? 's' : ''} due to inactivity.
          </p>
        </div>
        <div class="flex justify-end space-x-3">
          <button id="session-logout" class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">
            Logout Now
          </button>
          <button id="session-continue" class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded">
            Continue Session
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Add event listeners
    const continueBtn = modal.querySelector('#session-continue');
    const logoutBtn = modal.querySelector('#session-logout');

    continueBtn?.addEventListener('click', () => {
      document.body.removeChild(modal);
      this.resetTimer();
    });

    logoutBtn?.addEventListener('click', () => {
      document.body.removeChild(modal);
      this.handleLogout('User logged out from session warning');
    });
  }

  private handleLogout(_reason: string): void {
    // Clear all timers
    this.clearTimers();
    
    // Trigger logout through auth store
    const authStore = useAuthStore.getState();
    authStore.logout();
    
    // Remove warning modal if it exists
    const modal = document.querySelector('.fixed.inset-0.bg-black.bg-opacity-50');
    if (modal) {
      document.body.removeChild(modal);
    }
  }

  private clearTimers(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  private startActivityCheck(): void {
    this.checkIntervalId = setInterval(() => {
      this.checkTokenValidity();
    }, this.CHECK_INTERVAL);
  }

  private async checkTokenValidity(): Promise<void> {
    const token = localStorage.getItem('access_token');
    if (!token) {
      // Don't logout if we're not supposed to be authenticated
      // This allows login errors to be displayed without interference
      const authStore = useAuthStore.getState();
      if (authStore.isAuthenticated) {
        this.handleLogout('No access token found');
      }
      return;
    }

    try {
      // Parse JWT token to check expiry (basic check without verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      
      if (payload.exp <= currentTime) {
        this.handleLogout('Token expired');
        return;
      }

      // Check if token needs refresh (within threshold)
      const timeUntilExpiry = (payload.exp - currentTime) * 1000;
      if (timeUntilExpiry <= this.TOKEN_REFRESH_THRESHOLD) {
        // Attempt to refresh token by validating current user
        const authStore = useAuthStore.getState();
        const isValid = await authStore.validateToken();
        if (!isValid) {
          this.handleLogout('Token validation failed');
        }
      }
    } catch (error) {
      console.error('SessionManager: Error checking token validity:', error);
      this.handleLogout('Token validation error');
    }
  }

  public start(): void {
    if (this.isRunning) {
      return; // Already running, don't start again
    }
    
    this.isRunning = true;
    this.resetTimer();
    
    // Start the activity check interval if it's not already running
    if (!this.checkIntervalId) {
      this.startActivityCheck();
    }
  }

  public stop(): void {
    if (!this.isRunning) {
      return; // Already stopped, don't stop again
    }
    
    this.isRunning = false;
    this.clearTimers();
    this.warningShown = false;
    
    // Also clear the activity check interval
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  public extend(): void {
    this.resetTimer();
  }

  public getTimeUntilExpiry(): number {
    const timeSinceActivity = Date.now() - this.lastActivity;
    return Math.max(0, this.INACTIVITY_TIMEOUT - timeSinceActivity);
  }

  public isActive(): boolean {
    return this.getTimeUntilExpiry() > 0;
  }
}

// Export singleton instance
export const sessionManager = SessionManager.getInstance();
